// Per-entry reviewer decisions and the final-committer record for a queued-edits
// proposal Bundle, stored ON the Bundle so it is the self-contained source of
// truth: a page refresh rebuilds the review from the persisted Bundle alone, and
// no Task is required (the inline ai-chat case creates none). Per-entry decisions
// live on `Bundle.entry[*].extension`; the bundle-level final committer lives on
// `Bundle.meta.extension` (Bundle has no top-level `extension`).
//
// ##EntryHandle Each decided entry carries one complex extension — the entry
// it sits on is the key, so no separate handle field.

import type {
  Bundle,
  BundleEntry,
  Extension,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import {
  QUEUED_EDITS_DECISION_AT,
  QUEUED_EDITS_DECISION_BY,
  QUEUED_EDITS_DECISION_FROM_VERSION,
  QUEUED_EDITS_DECISION_OUTCOME,
  QUEUED_EDITS_ENTRY_DECISION_EXTENSION,
  QUEUED_EDITS_ENTRY_GROUP_EXTENSION,
  QUEUED_EDITS_ENTRY_INTENT_EXTENSION,
  QUEUED_EDITS_FINAL_COMMITTER_EXTENSION,
} from '#src/codeSystems.ts'
import { entryHandle, matchesHandle } from '#src/queued-edits/entry-handle.ts'
import type { ResourceDecision } from '#src/queued-edits/model.ts'
import { now } from '#src/utils.ts'

export interface BundleEntryDecision {
  // ##EntryHandle The decided entry's handle.
  entry: string
  decision: ResourceDecision
  by: Reference<Practitioner> | undefined
  at: string | undefined
  // The pinned versioned reference (`ResourceType/id/_history/{vid}`) the
  // entry's diff was computed against, recorded at apply time.
  fromVersion: Reference | undefined
}

function decisionExtension(
  decision: ResourceDecision,
  by: Reference<Practitioner>,
  at: string,
  fromVersion?: Reference
): Extension {
  return {
    url: QUEUED_EDITS_ENTRY_DECISION_EXTENSION,
    extension: [
      { url: QUEUED_EDITS_DECISION_OUTCOME, valueCode: decision },
      { url: QUEUED_EDITS_DECISION_BY, valueReference: by },
      { url: QUEUED_EDITS_DECISION_AT, valueDateTime: at },
      ...(fromVersion
        ? [
            {
              url: QUEUED_EDITS_DECISION_FROM_VERSION,
              valueReference: fromVersion,
            },
          ]
        : []),
    ],
  }
}

function readDecision(entry: BundleEntry): BundleEntryDecision | undefined {
  const ext = entry.extension?.find(
    (e) => e.url === QUEUED_EDITS_ENTRY_DECISION_EXTENSION
  )
  const handle = entryHandle(entry)
  if (!ext || !handle) {
    return undefined
  }
  const outcome = ext.extension?.find(
    (e) => e.url === QUEUED_EDITS_DECISION_OUTCOME
  )?.valueCode
  if (outcome !== 'approved' && outcome !== 'rejected') {
    return undefined
  }
  return {
    entry: handle,
    decision: outcome,
    by: ext.extension?.find((e) => e.url === QUEUED_EDITS_DECISION_BY)
      ?.valueReference as Reference<Practitioner> | undefined,
    at: ext.extension?.find((e) => e.url === QUEUED_EDITS_DECISION_AT)
      ?.valueDateTime,
    fromVersion: ext.extension?.find(
      (e) => e.url === QUEUED_EDITS_DECISION_FROM_VERSION
    )?.valueReference,
  }
}

// Record one reviewer decision for a single entry, capturing who decided and
// when. Idempotent per entry — re-deciding replaces the entry's prior decision
// extension (so toggling approve↔reject never stacks duplicates). `fromVersion`
// (optional) pins the version this entry's diff was computed against; supplied
// at apply time, omitted while toggling live. Mutates and returns the bundle.
export function setEntryDecision(
  bundle: Bundle,
  entry: string,
  decision: ResourceDecision,
  by: Reference<Practitioner>,
  at: string = now(),
  fromVersion?: Reference
): Bundle {
  const target = (bundle.entry ?? []).find((e) => matchesHandle(e, entry))
  if (!target) {
    return bundle
  }
  const others = (target.extension ?? []).filter(
    (e) => e.url !== QUEUED_EDITS_ENTRY_DECISION_EXTENSION
  )
  target.extension = [
    ...others,
    decisionExtension(decision, by, at, fromVersion),
  ]
  return bundle
}

// Clear any recorded decision for an entry (reviewer un-decided it). Mutates and
// returns the bundle; drops an emptied `entry.extension` array.
export function clearEntryDecision(bundle: Bundle, entry: string): Bundle {
  const target = (bundle.entry ?? []).find((e) => matchesHandle(e, entry))
  if (!target?.extension) {
    return bundle
  }
  const remaining = target.extension.filter(
    (e) => e.url !== QUEUED_EDITS_ENTRY_DECISION_EXTENSION
  )
  if (remaining.length > 0) {
    target.extension = remaining
  } else {
    delete target.extension
  }
  return bundle
}

// The recorded decisions keyed by entry handle, with who/when — the handles the
// review UI uses to rehydrate in-memory decision state on reload.
export function getEntryDecisions(bundle: Bundle): BundleEntryDecision[] {
  return (bundle.entry ?? []).flatMap((entry) => {
    const decision = readDecision(entry)
    return decision ? [decision] : []
  })
}

export function approvedEntryHandles(bundle: Bundle): string[] {
  return getEntryDecisions(bundle)
    .filter((d) => d.decision === 'approved')
    .map((d) => d.entry)
}

export function rejectedEntryHandles(bundle: Bundle): string[] {
  return getEntryDecisions(bundle)
    .filter((d) => d.decision === 'rejected')
    .map((d) => d.entry)
}

// The pinned versioned references each decided entry's diff was computed
// against — the baseline the completed-review diff reads. Both approved and
// rejected entries carry one when recorded at apply time.
export function entryFromVersions(bundle: Bundle): Reference[] {
  return getEntryDecisions(bundle)
    .map((d) => d.fromVersion)
    .filter((value): value is Reference => Boolean(value))
}

// Bulk-record the reviewer's per-entry decisions (apply time). `by`/`at` are the
// apply-time fallback attribution — used only for entries with no prior recorded
// decider (e.g. a decision driven entirely from the apply call without an
// intervening toggle). Entries already attributed to a reviewer keep that
// reviewer, so collaborative "who decided what" survives the apply.
// `fromVersionByEntry` (optional) maps an entry handle to the pinned version its
// diff was computed against — recorded for both approved and rejected entries.
export function recordEntryDecisions(
  bundle: Bundle,
  {
    approved,
    rejected,
    by,
    at = now(),
    fromVersionByEntry = {},
  }: {
    approved: string[]
    rejected: string[]
    by: Reference<Practitioner>
    at?: string
    fromVersionByEntry?: Record<string, Reference>
  }
): Bundle {
  // Prior decisions are keyed by each entry's canonical handle (its fullUrl).
  // Callers may pass either a fullUrl or a `request.url` handle, so resolve the
  // incoming handle to the same canonical key before looking up the prior.
  const priorByEntry = new Map(
    getEntryDecisions(bundle).map((d) => [d.entry, d] as const)
  )
  const canonicalHandle = (handle: string): string | undefined => {
    const target = (bundle.entry ?? []).find((e) => matchesHandle(e, handle))
    return target ? entryHandle(target) : undefined
  }
  const record = (entry: string, decision: ResourceDecision): void => {
    const prior = priorByEntry.get(canonicalHandle(entry) ?? entry)
    // A fresh version supersedes any prior one; otherwise keep what was recorded
    // so re-recording an unchanged decision doesn't drop the pin.
    const fromVersion = fromVersionByEntry[entry] ?? prior?.fromVersion
    // Preserve the original decider/timestamp when the same decision was already
    // recorded by a reviewer; only fall back to the apply-time attribution when
    // nothing was recorded for this entry yet.
    if (prior?.by && prior.decision === decision) {
      setEntryDecision(
        bundle,
        entry,
        decision,
        prior.by,
        prior.at ?? at,
        fromVersion
      )
      return
    }
    setEntryDecision(bundle, entry, decision, by, at, fromVersion)
  }
  for (const entry of approved) {
    record(entry, 'approved')
  }
  for (const entry of rejected) {
    record(entry, 'rejected')
  }
  return bundle
}

// Record who executed the final application of the approved changes, on
// `Bundle.meta.extension`. The matching ExtendedProvenance is the durable audit
// record; this keeps the Bundle self-describing for the review summary without
// an extra Provenance read. Mutates and returns the bundle.
export function setFinalCommitter(
  bundle: Bundle,
  by: Reference<Practitioner>,
  at: string = now()
): Bundle {
  const others = (bundle.meta?.extension ?? []).filter(
    (e) => e.url !== QUEUED_EDITS_FINAL_COMMITTER_EXTENSION
  )
  bundle.meta = {
    ...bundle.meta,
    extension: [
      ...others,
      {
        url: QUEUED_EDITS_FINAL_COMMITTER_EXTENSION,
        extension: [
          { url: QUEUED_EDITS_DECISION_BY, valueReference: by },
          { url: QUEUED_EDITS_DECISION_AT, valueDateTime: at },
        ],
      },
    ],
  }
  return bundle
}

function finalCommitterExtension(bundle: Bundle): Extension | undefined {
  return bundle.meta?.extension?.find(
    (e) => e.url === QUEUED_EDITS_FINAL_COMMITTER_EXTENSION
  )
}

export function getFinalCommitter(
  bundle: Bundle
): Reference<Practitioner> | undefined {
  return finalCommitterExtension(bundle)?.extension?.find(
    (e) => e.url === QUEUED_EDITS_DECISION_BY
  )?.valueReference as Reference<Practitioner> | undefined
}

export function getFinalCommittedAt(bundle: Bundle): string | undefined {
  return finalCommitterExtension(bundle)?.extension?.find(
    (e) => e.url === QUEUED_EDITS_DECISION_AT
  )?.valueDateTime
}

// A deep clone of the bundle with every per-entry review-display extension
// removed (decisions, proposer intent labels, and group labels) — the apply
// bundle, so review metadata never reaches the server write. Drops an emptied
// `entry.extension` array. Leaves `request`/`resource` untouched, so the
// stripped bundle stays executable.
export function stripEntryDecisions(bundle: Bundle): Bundle {
  const clone = structuredClone(bundle)
  for (const entry of clone.entry ?? []) {
    if (!entry.extension) {
      continue
    }
    const remaining = entry.extension.filter(
      (e) =>
        e.url !== QUEUED_EDITS_ENTRY_DECISION_EXTENSION &&
        e.url !== QUEUED_EDITS_ENTRY_INTENT_EXTENSION &&
        e.url !== QUEUED_EDITS_ENTRY_GROUP_EXTENSION
    )
    if (remaining.length > 0) {
      entry.extension = remaining
    } else {
      delete entry.extension
    }
  }
  return clone
}
